import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, Lock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { publicApi } from "@/lib/api";
import { usePublicSettings } from "@/hooks/usePublicSettings";
import { APP_VERSION } from "@/version";
import type { NavItem } from "@/types";
import { useConsent } from "@/contexts/ConsentContext";

function formatStationDateTime() {
  const now = new Date();
  return now.toLocaleString("en-US", {
    timeZone: "America/Phoenix",
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function Footer() {
  const currentYear = new Date().getFullYear();
  const [phoenixTime, setPhoenixTime] = useState(() => formatStationDateTime());
  const { data: settings } = usePublicSettings();
  const { openPreferences } = useConsent();

  const { data: footerLinksRaw = [] } = useQuery<NavItem[]>({
    queryKey: ["footer-nav"],
    queryFn: async () => {
      const data = (await publicApi.footerNav.getAll()).data;
      return data.sort((a: NavItem, b: NavItem) => (a.displayOrder || 0) - (b.displayOrder || 0));
    },
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setPhoenixTime(formatStationDateTime());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Filter FOOTER placement items
  const footerLinks = footerLinksRaw.filter((link) => link.placement === "FOOTER");

  return (
    <footer className="bg-slate-100 border-t border-slate-200">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-sm border border-slate-200">
                <img
                  src={settings?.logoCircleUrl || settings?.logoUrl || "/images/sunny/logo.png"}
                  alt={`${settings?.siteName || "Sunny 106.5"} logo`}
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <span className="font-logo text-xl font-bold text-slate-900">
                {settings?.siteName || "Sunny 106.5"}
              </span>
            </Link>
            <p className="text-slate-700 text-sm mb-4">
              {settings?.siteTagline || "Palm Springs Radio & Events"}
            </p>
            <p className="text-sm text-slate-500">Palm Springs time: {phoenixTime}</p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {footerLinks.length > 0 ? (
                footerLinks.map((link) => (
                  <li key={link.id}>
                    <Link 
                      to={link.url} 
                      className="text-sm text-slate-700 hover:text-primary hover:underline transition-colors underline-offset-2"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))
              ) : (
                <>
                  <li>
                    <Link to="/" className="text-sm text-slate-700 hover:text-primary hover:underline transition-colors underline-offset-2">
                      Home
                    </Link>
                  </li>
                  <li>
                    <Link to="/about" className="text-sm text-slate-700 hover:text-primary hover:underline transition-colors underline-offset-2">
                      About
                    </Link>
                  </li>
                  <li>
                    <Link to="/radio" className="text-sm text-slate-700 hover:text-primary hover:underline transition-colors underline-offset-2">
                      Radio
                    </Link>
                  </li>
                  <li>
                    <Link to="/events" className="text-sm text-slate-700 hover:text-primary hover:underline transition-colors underline-offset-2">
                      Events
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-4">Connect</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/contact" className="text-sm text-slate-700 hover:text-primary hover:underline transition-colors underline-offset-2">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/newsletter" className="text-sm text-slate-700 hover:text-primary hover:underline transition-colors underline-offset-2">
                  Newsletter
                </Link>
              </li>
              <li>
                <Link to="/member/login" className="text-sm text-slate-700 hover:text-primary hover:underline transition-colors underline-offset-2">
                  Lucky Duck Club
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-4">Resources</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy" className="text-sm text-slate-700 hover:text-primary hover:underline transition-colors underline-offset-2">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm text-slate-700 hover:text-primary hover:underline transition-colors underline-offset-2">
                  Terms of Service
                </Link>
              </li>
              <li>
                <button type="button" onClick={openPreferences} className="text-sm text-slate-700 hover:text-primary hover:underline transition-colors underline-offset-2">
                  Cookie Preferences
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-200 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4 text-sm text-slate-600">
              <span>&copy; {currentYear} {settings?.siteName || "Sunny 106.5"}. All rights reserved.</span>
              <span className="text-slate-400">|</span>
              <span className="text-slate-500">v{APP_VERSION}</span>
            </div>
            <div className="flex items-center gap-4">
              <p className="text-sm text-slate-600 flex items-center gap-1">
                Made with <Heart className="w-4 h-4 text-red-500 fill-current" /> by{" "}
                <a 
                  href="https://1lpro.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 hover:underline font-medium underline-offset-2"
                >
                  1LPro.com
                </a>
              </p>
              <Link 
                to="/admin" 
                className="p-2 text-slate-400 hover:text-primary transition-colors rounded-lg hover:bg-slate-200"
                title="Admin Login"
              >
                <Lock className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
